/** Canonical control presentation uses the existing scope/Query lifecycle. */
import { useLayoutEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { companySoftwareBillingApi as api } from "@bw/shared/api";
import type { CompanySoftwareBillingScope, CompanyPurchaseId, CompanyRenewalControlReference,
  CompanyRenewalControlOriginal, CompanyRenewalControlPage, CompanyRenewalControlPageRequest,
  CompanyRenewalControlRecoveryResult } from "@bw/shared/api";
import type { CompanySoftwareRenewalControlHooks } from "./companyPurchaseContracts";
import { checkAgreement, exactKeys, uuid, useScopeLease, useScopedQuery,
  useGuardedScopedMutation, CompanyBillingRequestError, companyBillingQueryKey } from "./useModuleBilling";
import { checkCompanyLicence } from "./useModulePurchases";

const validTime = (value: unknown): boolean => typeof value === "string"
  && /^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/i.test(value) && Number.isFinite(Date.parse(value));

function original(value: CompanyRenewalControlOriginal): void {
  exactKeys(value, ["operation_id", "action", "admitted_licence_revision", "created_at"]);
  if (!uuid(value.operation_id) || !["stop_renewal", "restore_renewal"].includes(value.action)
    || !Number.isSafeInteger(value.admitted_licence_revision) || value.admitted_licence_revision < 1
    || !validTime(value.created_at)) throw new Error("Invalid canonical renewal original");
}

function reference(value: CompanyRenewalControlReference): void {
  exactKeys(value, ["operation_id", "action", "admitted_licence_revision", "created_at", "operation_status"]);
  const { operation_status, ...pins } = value;
  original(pins);
  if (!["pending", "completed", "failed", "uncertain"].includes(operation_status)) throw new Error("Invalid canonical renewal status");
}

function requestPage(value: CompanyRenewalControlPageRequest): void {
  exactKeys(value, ["limit", "offset"]);
  if (!Number.isSafeInteger(value.limit) || value.limit < 1 || value.limit > 100
    || !Number.isSafeInteger(value.offset) || value.offset < 0) throw new Error("Invalid canonical renewal page request");
}

export function checkRenewalControlPage(value: CompanyRenewalControlPage, scope: CompanySoftwareBillingScope,
  purchaseId: CompanyPurchaseId, page: CompanyRenewalControlPageRequest): void {
  requestPage(page);
  exactKeys(value, ["org_id", "environment", "acceptance_id", "purchase_id", "items", "total", "limit", "offset"]);
  checkAgreement(value, scope);
  if (![value.org_id, value.acceptance_id, value.purchase_id].every(uuid) || value.purchase_id !== purchaseId || !Array.isArray(value.items)
    || !Number.isSafeInteger(value.total) || value.total < 0 || value.limit !== page.limit || value.offset !== page.offset
    || value.items.length > page.limit || value.items.length > Math.max(0, value.total - page.offset)
    || new Set(value.items.map(item => item.operation_id)).size !== value.items.length) throw new Error("Invalid canonical renewal page");
  value.items.forEach(reference);
}

function knownOriginal(client: QueryClient, scope: CompanySoftwareBillingScope, purchaseId: CompanyPurchaseId,
  expected: CompanyRenewalControlOriginal): void {
  // Reuse validated canonical Query facts, not a parallel browser registry.
  for (const [, page] of client.getQueriesData<CompanyRenewalControlPage>({
    queryKey: [...companyBillingQueryKey(scope), "listRenewalControls", purchaseId],
  })) {
    if (!page) continue;
    checkRenewalControlPage(page, scope, purchaseId, { limit: page.limit, offset: page.offset });
    const prior = page.items.find(item => item.operation_id === expected.operation_id);
    if (prior && (prior.action !== expected.action || prior.admitted_licence_revision !== expected.admitted_licence_revision
      || prior.created_at !== expected.created_at)) throw new Error("Canonical renewal original changed");
  }
}

export function checkRenewalControlRecovery(value: CompanyRenewalControlRecoveryResult, scope: CompanySoftwareBillingScope,
  purchaseId: CompanyPurchaseId, expected: CompanyRenewalControlOriginal): void {
  exactKeys(value, ["org_id", "environment", "acceptance_id", "purchase_id", "control", "resolution", "reason", "licence"]);
  checkAgreement(value, scope); reference(value.control); original(expected);
  if (![value.org_id, value.acceptance_id, value.purchase_id].every(uuid) || value.purchase_id !== purchaseId || value.control.operation_id !== expected.operation_id
    || value.control.action !== expected.action || value.control.admitted_licence_revision !== expected.admitted_licence_revision
    || value.control.created_at !== expected.created_at || value.licence === null) throw new Error("Foreign canonical renewal result");
  checkCompanyLicence(value.licence, scope, purchaseId);
  const status = value.control.operation_status;
  if (value.resolution === "original_result") {
    if (!(status === "completed" && value.reason === null || status === "failed" && value.reason === "original_failed"))
      throw new Error("Invalid original renewal resolution");
  } else if (value.resolution === "observed_current_effect") {
    if (status !== "uncertain" || value.reason !== "original_outcome_unproven") throw new Error("Invalid retained renewal effect");
  } else if (value.resolution !== "unresolved" || !["pending", "uncertain"].includes(status)
    || !["awaiting_provider", "provider_unavailable", "original_configuration_unavailable", "assurance_expired",
      "effect_mismatch", "control_superseded", "schedule_effect_unproven"].includes(value.reason ?? "")) {
    throw new Error("Invalid unresolved renewal result");
  }
}

function useSelectionLease(scope: CompanySoftwareBillingScope, purchaseId: CompanyPurchaseId | null): () => void {
  const live = useRef({ purchaseId, active: true }); live.current.purchaseId = purchaseId;
  useLayoutEffect(() => { live.current.active = true; return () => { live.current.active = false; }; }, [purchaseId]);
  return () => {
    if (!live.current.active || purchaseId === null || !uuid(purchaseId) || live.current.purchaseId !== purchaseId)
      throw new CompanyBillingRequestError(scope, "listRenewalControls", 503, "COMPANY_SELECTION_CHANGED");
  };
}

export function useCompanyRenewalControls(scope: CompanySoftwareBillingScope, billingFenceRevision: number,
  suspendAutomaticReads: boolean, purchaseId: CompanyPurchaseId | null, page: CompanyRenewalControlPageRequest,
): ReturnType<CompanySoftwareRenewalControlHooks["useCompanyRenewalControls"]> {
  const client = useQueryClient(), selected = useSelectionLease(scope, purchaseId);
  return useScopedQuery(scope, billingFenceRevision, "listRenewalControls", [purchaseId, page.limit, page.offset],
    !suspendAutomaticReads && purchaseId !== null, async () => {
      selected(); requestPage(page);
      if (purchaseId === null) throw new Error("No original purchase selected");
      const value = await api.listRenewalControls(scope.orgId, purchaseId, scope.environment, page);
      selected(); checkRenewalControlPage(value, scope, purchaseId, page);
      value.items.forEach(item => knownOriginal(client, scope, purchaseId, item)); return value;
    });
}

export function useResumeCompanyRenewalControl(scope: CompanySoftwareBillingScope, billingFenceRevision: number,
  purchaseId: CompanyPurchaseId | null,
): ReturnType<CompanySoftwareRenewalControlHooks["useResumeCompanyRenewalControl"]> {
  const client = useQueryClient();
  const scoped = useScopeLease(scope, billingFenceRevision), selected = useSelectionLease(scope, purchaseId);
  const ensure = () => { scoped(); selected(); };
  return useGuardedScopedMutation(scope, "resumeRenewalControl", ensure, async (expected: CompanyRenewalControlOriginal, requestLease) => {
    original(expected);
    if (purchaseId === null) throw new Error("No original purchase selected");
    knownOriginal(client, scope, purchaseId, expected);
    const value = await api.resumeRenewalControl(scope.orgId, purchaseId, expected.operation_id, scope.environment);
    requestLease(); checkRenewalControlRecovery(value, scope, purchaseId, expected);
    knownOriginal(client, scope, purchaseId, expected); return value;
  });
}
